import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, TitleRow, Event } from 'components/lib';
import { Box, Grid, TextField, Button, Checkbox, IconButton, Typography, ToggleButton, ToggleButtonGroup, Tabs, Tab, Stack } from '@mui/material';
import { 
    Block as BlockIcon, 
    ChecklistOutlined as WhiteListIcon, 
    Delete as DeleteIcon 
} from '@mui/icons-material';

import { Loader } from 'components/lib'; 
import { Card } from 'mbd-components/card';
import { Text } from 'mbd-components/text';
import { PrimaryButton } from 'mbd-components/primary-button';  

import { fetchList, updateList,fetchItemWarpcastLinkByHash, fetchUserWarpcastLinkByFid } from './helpers';
import { ItemInput } from './item_input';

export function Moderation() {
    const [listType, setListType] = useState('blocklist'); // 'blocklist' or 'whitelist'
    const [itemType, setItemType] = useState('items'); // 'items' or 'users'
    const [inputValue, setInputValue] = useState('');
    const [list, setList] = useState([]);
    const [itemBlockList, setItemBlockList] = useState([]);
    const [itemWhiteList, setItemWhiteList] = useState([]);
    const [userBlockList, setUserBlockList] = useState([]);
    const [userWhiteList, setUserWhiteList] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]); // To track selected items
    const [loading, setLoading] = useState(false); 
    const [listLoading, setListLoading] = useState(false);

    const authContext = useContext(AuthContext);

    useEffect(() => {
        setInputValue(''); // Clear input when tab changes
        loadAllLists();
        setSelectedItems([]); // Reset selected items when switching between tabs
    }, []);

    useEffect(() => {
        updateDisplayList();
    }, [listType, itemType, itemBlockList, itemWhiteList, userBlockList, userWhiteList]);

    const updateDisplayList = () => {
        if (listType === 'blocklist') {
            setList(itemType === 'items' ? itemBlockList : userBlockList);
        } else {
            setList(itemType === 'items' ? itemWhiteList : userWhiteList);
        }
    }

    const loadAllLists = async (itemType) => {
        setLoading(true);
        let list = await fetchList('blocklist', 'items');
        setItemBlockList(list);
        list = await fetchList('blocklist', 'users');
        setUserBlockList(list);
        list = await fetchList('whitelist', 'items');
        setItemWhiteList(list);
        list = await fetchList('whitelist', 'users');
        setUserWhiteList(list);
        setLoading(false);
    };

    const handleTabSelection = (event, newValue) => {
        setListType(newValue);
        setInputValue('');
    };

    const handleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(item => item !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handlePreview = async (id, type) => {
        let urlToOpen = null;
        if (type === 'item') {
            urlToOpen = await fetchItemWarpcastLinkByHash(id);
        } else {
            urlToOpen = await fetchUserWarpcastLinkByFid(id);
        }

        if (urlToOpen) {
            // workaround for when opening url doesn't work in some browsers
            setTimeout(() => {
                window.open(urlToOpen, "_blank");
              }, 10);
            // window.open(urlToOpen, '_blank', "noopener,noreferrer");
        } else {
            console.error('No URL to open');
        }
    };

    const updateCurrentList = async(list) => {
        if (listType === 'blocklist') {
            if (itemType === 'items') {
                setItemBlockList(list);
            } else {
                setUserBlockList(list);
            }
        } else {
            if (itemType === 'items') {
                setItemWhiteList(list);
            } else {
                setUserWhiteList(list);
            }
        }
        setListLoading(true);
        await updateList(listType, itemType, list);
        setListLoading(false);
        setList(list);
    }

    const handleAdd = async (listType, itemType) => {
        if (inputValue) {
            const updatedList = [...list, inputValue];
            updateCurrentList(updatedList);
        }
    };

    const handleDeleteSelected = async (id) => {
        const updatedList = list.filter(item => item !== id);
        updateCurrentList(updatedList);
    };

    const handleDeleteSelection = async () => {
        const updatedList = list.filter(item => !selectedItems.includes(item));
        updateCurrentList(updatedList);
    };

    const handleDeleteAll = async () => {
        const updatedList = [];
        updateCurrentList(updatedList);
    };

    const ListTypeSelector = () =>
        <Tabs
            value={listType}
            onChange={handleTabSelection}
            aria-label="mode selection tabs"
        >
            <Tab icon={<BlockIcon />} iconPosition="start" label="Block List" value="blocklist" />
            <Tab icon={<WhiteListIcon />} iconPosition="start" label="White List" value="whitelist" />
        </Tabs>

    const ItemTypeSelector = () =>
        <Box my={4}>
            <ToggleButtonGroup
                value={itemType}
                exclusive
                onChange={(e, newValue) => {
                    setInputValue(''); 
                    setItemType(newValue)
                }}
                aria-label="items and users toggle"
            >
                <ToggleButton value="items" aria-label="items">
                    Items
                </ToggleButton>
                <ToggleButton value="users" aria-label="users">
                    Users
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>



    const ItemRow = ({item}) => 
        <Stack direction="row" spacing={2}>
            <Stack direction="row" spacing={1} flexGrow={1}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexGrow: 1 }}>
                    <Checkbox
                        checked={selectedItems.includes(item)}
                        onChange={() => handleSelectItem(item)}
                    />
                    <Text>{item}</Text>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'flex-end', mt: { xs: 1, md: 0 } }}>
                    <Button variant='text'
                        onClick={() => handlePreview(item, itemType === 'items' ? 'item' : 'user'   )}
                    >
                        View
                    </Button>
                    <IconButton onClick={() => handleDeleteSelected(item)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Stack>
        </Stack>

    return (
        <Grid container justifyContent="center" style={{ minHeight: '100vh' }}>
            <Grid item xs={12} sm={10} md={8}>
                {loading ? (
                    <Loader /> 
                ) : (
                    <>
                        <Card >
                            <ListTypeSelector />
                            <Box my={2}>
                                <Text>
                                    {
                                        listType === 'blocklist' ?
                                        'Block lists are used to prevent items and users from showing in your organization\'s feeds.' :
                                        'White lists are used to specify items and users that your organization trusts and should always be allowed to show on your organization\'s feeds, regardless of whether they are tagged as spammy by the network and by our models.'
                                    }
                                </Text>
                            </Box>
                            <ItemTypeSelector />
                            <ItemInput 
                                itemType={itemType}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onPreview={() => handlePreview(inputValue, itemType === 'items' ? 'item' : 'user')}
                                onAdd={() => handleAdd(listType, itemType)}
                            />


                            {/* List of Items/Users */}
                            <Box mt={4}>
                                {list.length > 0 ? (
                                    list.map((item) => ( <ItemRow item={item} />))    
                                ) : (
                                    <Text variant='body' color='gray-light'>
                                        No {itemType === 'items' ? 'items' : 'users'} in the list
                                    </Text>
                                )}
                            </Box>

                            {/* Delete All or Delete Selection Button */}
                            {list.length > 0 && (
                                <Box mt={4}>
                                    <PrimaryButton
                                        onClick={selectedItems.length > 0 ? handleDeleteSelection : handleDeleteAll}
                                    >
                                        {selectedItems.length > 0 ? 'Delete Selection' : 'Delete All'}
                                    </PrimaryButton>
                                </Box>
                            )}
                        </Card>
                    </>
                )}
            </Grid>
        </Grid>
    );
}